/**
 * Paying for a card booking: a hold on the card now (taken when the trip's done), or for
 * trips more than a few days ahead, saving the card to charge nearer the time. Also where
 * a customer pays again when the bank turned a payment down.
 */

import { ApiError, errorMessage, get, post } from "@taxi/mobile-web/api";
import { money, phone, when } from "@taxi/mobile-web/format";
import { useResource } from "@taxi/mobile-web/hooks";
import { ShieldIcon } from "@taxi/mobile-web/icons";
import { navigate, useLocation } from "@taxi/mobile-web/router";
import { Button, Loading, Notice, TopBar, useToast } from "@taxi/mobile-web/ui";
import { useEffect, useRef, useState } from "react";
import { PaymentForm, type PaymentFormHandle } from "../components/payment-form";
import { useSession } from "../lib/session";
import type { Booking, PaymentSheet } from "../lib/types";

/** Stripe's own script; anything else is the payments stub, whose card form says it's a test. */
const STRIPE_JS = "https://js.stripe.com/";

const sheetKey = (bookingId: string) => `pay:${bookingId}`;

export function savePaymentSheet(bookingId: string, sheet: PaymentSheet) {
  try {
    sessionStorage.setItem(sheetKey(bookingId), JSON.stringify(sheet));
  } catch {
    // Kept in memory below instead.
  }
  memory.set(bookingId, sheet);
}

const memory = new Map<string, PaymentSheet>();

function storedSheet(bookingId: string): PaymentSheet | null {
  try {
    const value = sessionStorage.getItem(sheetKey(bookingId));
    if (value) return JSON.parse(value) as PaymentSheet;
  } catch {
    // Fall through.
  }
  return memory.get(bookingId) ?? null;
}

function forgetSheet(bookingId: string) {
  memory.delete(bookingId);
  try {
    sessionStorage.removeItem(sheetKey(bookingId));
  } catch {
    // Nothing to forget.
  }
}

export function PayScreen({ id }: { id: string }) {
  const { query } = useLocation();
  const booking = useResource(() => get<Booking>(`/customer/bookings/${id}`), [id]);
  const [sheet, setSheet] = useState<PaymentSheet | null>(() => storedSheet(id));
  const [sheetError, setSheetError] = useState<string | null>(null);

  // A booking whose payment failed later (e.g. the bank wanted to check with them) gets a new sheet.
  const b = booking.data;
  useEffect(() => {
    if (!b || sheet) return;
    if (b.status !== "pending_payment" && b.payment?.status === "failed") {
      post<PaymentSheet>(`/customer/bookings/${id}/retry-payment`)
        .then((fresh) => {
          savePaymentSheet(id, fresh);
          setSheet(fresh);
        })
        .catch((error) => setSheetError(errorMessage(error)));
    }
  }, [b, sheet, id]);

  // Back from a bank's page (a payment method that left the app): check how it went.
  const returned = query.has("redirect_status");

  if (booking.error) return <PayProblem message={errorMessage(booking.error)} id={id} />;
  if (!b) return <Loading />;
  const paid =
    (b.status !== "pending_payment" && b.payment?.status !== "failed") ||
    b.status === "cancelled" ||
    b.paymentMethod !== "card";
  if (paid) {
    navigateSoon(`/bookings/${id}`);
    return <Loading />;
  }
  if (sheetError) return <PayProblem message={sheetError} id={id} />;
  if (!sheet) {
    return b.status === "pending_payment" ? (
      <PayProblem
        id={id}
        message="This payment page has expired. Cancel the booking and book again, or call us."
        cancellable
      />
    ) : (
      <Loading />
    );
  }
  return <PayForm booking={b} sheet={sheet} returned={returned} />;
}

/** Navigating during render upsets React; do it straight after. */
function navigateSoon(to: string) {
  setTimeout(() => navigate(to, { replace: true }));
}

function PayProblem({
  message,
  id,
  cancellable = false,
}: {
  message: string;
  id: string;
  cancellable?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="pay">
      <TopBar title="Payment" back={`/bookings/${id}`} />
      <div className="screen-body">
        <Notice tone="warning">{message}</Notice>
        {cancellable && (
          <Button
            variant="secondary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              await post(`/customer/bookings/${id}/cancel`, {
                reason: "Payment page expired",
              }).catch(() => undefined);
              navigate("/", { replace: true });
            }}
          >
            Cancel booking
          </Button>
        )}
      </div>
    </div>
  );
}

function PayForm({
  booking,
  sheet,
  returned,
}: {
  booking: Booking;
  sheet: PaymentSheet;
  returned: boolean;
}) {
  const { firmPhone, config } = useSession();
  const toast = useToast();
  const form = useRef<PaymentFormHandle>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  async function confirmWithApi(): Promise<boolean> {
    try {
      await post<Booking>(`/customer/bookings/${booking.id}/confirm-payment`);
      forgetSheet(booking.id);
      navigate(`/bookings/${booking.id}`, { replace: true });
      return true;
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "payment_incomplete"
          ? `${err.message}. If your bank asked you to approve it, try again.`
          : errorMessage(err),
      );
      return false;
    }
  }

  // Coming back from a bank's page: the payment may already have gone through.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on the way back.
  useEffect(() => {
    if (returned) {
      setBusy(true);
      void confirmWithApi().finally(() => setBusy(false));
    }
  }, []);

  async function pay() {
    setBusy(true);
    setError(null);
    const problem = await form.current!.confirm();
    if (problem) {
      setError(problem);
      setBusy(false);
      return;
    }
    if (!(await confirmWithApi())) setBusy(false);
  }

  async function cancel() {
    setCancelling(true);
    try {
      await post(`/customer/bookings/${booking.id}/cancel`, { reason: "Didn't finish paying" });
      forgetSheet(booking.id);
      toast("Booking cancelled. You haven't been charged.");
      navigate("/", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setCancelling(false);
    }
  }

  const retrying = booking.status !== "pending_payment";
  // Stripe's test mode needs saying; the payments stub's card form says so itself.
  const testMode =
    sheet.publishableKey?.startsWith("pk_test_") && config.stripeJsUrl.startsWith(STRIPE_JS);
  return (
    <div className="pay">
      <TopBar title={retrying ? "Pay again" : "Pay by card"} back={`/bookings/${booking.id}`} />
      <div className="screen-body">
        <div className="pay-amount">
          <span className="overline">{sheet.mode === "payment" ? "To pay" : "To book"}</span>
          <strong>{money(sheet.amountPence)}</strong>
          <small>
            Booking {booking.number} · {booking.isAsap ? "Now" : when(new Date(booking.pickupAt))}
          </small>
        </div>
        {retrying && booking.payment?.failureReason && (
          <Notice tone="warning">
            We couldn't take payment: {booking.payment.failureReason} Please try again, or use
            another card.
          </Notice>
        )}
        <p className="pay-explainer">
          <ShieldIcon size={18} />
          {sheet.mode === "payment"
            ? booking.isEstimate
              ? "We'll hold this on your card and only take what the meter says at the end, up to this amount. Nothing is taken if you cancel."
              : "We'll hold this on your card and only take it once your trip is done. Nothing is taken if you cancel."
            : "Your trip is a while away, so we'll save your card now and take payment the day before."}
        </p>
        {testMode && (
          <Notice tone="info">
            Test payments: no real money moves. Use card 4242 4242 4242 4242, any future date and
            any CVC.
          </Notice>
        )}
        <PaymentForm
          ref={form}
          sheet={sheet}
          returnUrl={`${window.location.origin}/bookings/${booking.id}/pay`}
          failedCard={
            retrying && booking.payment
              ? { brand: booking.payment.cardBrand, last4: booking.payment.cardLast4 }
              : null
          }
          onReady={setReady}
        />
        {error && <Notice tone="error">{error}</Notice>}
        <p className="fine-print">
          Problems paying? Call us on <a href={`tel:${firmPhone}`}>{phone(firmPhone)}</a>.
        </p>
      </div>
      <div className="footer-bar">
        <Button
          loading={busy}
          disabled={!ready || cancelling}
          onClick={pay}
          value={sheet.mode === "payment" ? money(sheet.amountPence) : undefined}
        >
          {sheet.mode === "payment" ? "Pay" : "Confirm booking"}
        </Button>
        {!retrying && (
          <Button variant="ghost" loading={cancelling} disabled={busy} onClick={cancel}>
            Cancel booking
          </Button>
        )}
      </div>
    </div>
  );
}
